// Enterprise SSO — the client-side vocabulary both apps share (spec
// docs/specs/2026-10-15-enterprise-sso-entra-design.md). The web and the native
// app run the same email-first sign-in; these are the rules they must agree on.

/**
 * Why a company sign-in did not finish — the closed set Trusstor and the d-orca
 * backend put on `?error=`. Every value has a translation, so anything else is
 * read as `sso_failed` rather than shown raw.
 */
export const SSO_LOGIN_ERRORS = [
  'not_invited',
  'cancelled',
  'sso_not_configured',
  'login_expired',
  'account_mismatch',
  'sso_failed',
] as const;
export type SsoLoginError = (typeof SSO_LOGIN_ERRORS)[number];

/** `null` when there is no error to show; an unknown code is a generic failure. */
export function toSsoLoginError(raw: string | null | undefined): SsoLoginError | null {
  if (raw === null || raw === undefined || raw === '') return null;
  return (SSO_LOGIN_ERRORS as readonly string[]).includes(raw) ? (raw as SsoLoginError) : 'sso_failed';
}

/**
 * Whether a typed sign-in identifier is an email (ask which way it signs in) or
 * a username (straight to the password — only password users have usernames to
 * type). The backend's own split is the same crude shape test.
 */
export function isEmailIdentifier(identifier: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier.trim());
}

/** What `POST /api/auth/sso/discover` answers. */
export interface ISsoDiscovery {
  sso: boolean;
  /** The company's name for the "Continue to …" button. Only when `sso`. */
  displayName?: string;
  /** The company's logo. Only when `sso` and the company has one. */
  logoUrl?: string;
}

/** Only an absolute http(s) URL may become an image source. */
function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/** Read a discover response leniently: anything unexpected means "password", today's flow. */
export function parseSsoDiscovery(data: unknown): ISsoDiscovery {
  if (typeof data !== 'object' || data === null) return { sso: false };
  const { sso, displayName, logoUrl } = data as Record<string, unknown>;
  if (sso !== true) return { sso: false };
  return {
    sso: true,
    ...(typeof displayName === 'string' && displayName !== '' ? { displayName } : {}),
    ...(isHttpUrl(logoUrl) ? { logoUrl } : {}),
  };
}
